/**
 * Admin → Database Connections.
 * v1.55 — Mike's request: after a successful login, show which databases
 * we're talking to (SQL Server + Mongo) WITHOUT exposing passwords, so
 * we can verify the right environment at a glance.
 *
 * Auth: any signed-in user can hit this. Passwords are never sent
 * back from the API (always masked to ********).
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { ArrowLeft, ArrowsClockwise, CheckCircle, XCircle, Database, Copy, Check } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const card = "bg-white border border-[#E5E5E5] p-5";
const label = "text-[10px] font-bold uppercase tracking-[0.15em] text-[#A3A3A3]";
const value = "text-sm font-mono text-[#0A0A0A] break-all";

function Row({ k, v, mask }) {
  const [copied, setCopied] = useState(false);
  if (!v) return null;
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(v);
      setCopied(true);
      toast.success(`${k} copied`);
      setTimeout(() => setCopied(false), 1200);
    } catch { /* swallow */ }
  };
  return (
    <div className="flex items-start gap-4 py-2 border-b border-[#F5F5F5] last:border-b-0">
      <div className={`${label} w-40 flex-shrink-0 mt-0.5`}>{k}</div>
      <div className={`${value} flex-1`} data-testid={`db-field-${k.replace(/\s+/g, "-").toLowerCase()}`}>{mask || v}</div>
      {!mask && (
        <button
          type="button"
          onClick={onCopy}
          className="text-[#A3A3A3] hover:text-[#0055FF] flex-shrink-0"
          title={`Copy ${k}`}
        >
          {copied ? <Check size={14} weight="bold" /> : <Copy size={14} />}
        </button>
      )}
    </div>
  );
}

export default function DbConnectionsPage() {
  const navigate = useNavigate();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    setRefreshing(true);
    try {
      const r = await api.get("/users/me/db-connections");
      setInfo(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't load connection info");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { load(); }, []);

  return (
    <div className="min-h-screen bg-[#F5F5F5]">
      <div className="border-b border-[#E5E5E5] bg-white px-6 py-3 flex items-center justify-between">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-sm text-[#525252] hover:text-[#0055FF]" data-testid="db-connections-back">
          <ArrowLeft size={16} /> Back
        </button>
        <Button
          onClick={load}
          disabled={refreshing}
          className="bg-[#0055FF] hover:bg-[#0040CC] text-white font-bold rounded-none h-9 px-4 text-xs"
          data-testid="db-connections-refresh"
        >
          <ArrowsClockwise size={14} className={`mr-2 ${refreshing ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-8 space-y-6">
        <div>
          <h1 className="text-2xl font-black tracking-tight text-[#0A0A0A] mb-1" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            Database Connections
          </h1>
          <p className="text-sm text-[#525252]">
            Read-only view of where this app is currently storing data. Passwords are never exposed.
          </p>
          {info?.environment && (
            <span
              className={`inline-block mt-2 text-[10px] font-bold uppercase tracking-[0.15em] px-2 py-0.5 ${
                info.environment === "prod" ? "bg-[#FEE2E2] text-[#991B1B]" : "bg-[#DBEAFE] text-[#1E40AF]"
              }`}
              data-testid="db-environment-badge"
            >
              Environment: {info.environment}
            </span>
          )}
        </div>

        {loading ? (
          <div className="text-sm text-[#A3A3A3]">Loading…</div>
        ) : (
          <>
            {/* SQL Server */}
            <div className={card} data-testid="sql-server-card">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Database size={18} className="text-[#0055FF]" weight="duotone" />
                  <h2 className="text-base font-bold text-[#0A0A0A]">SQL Server</h2>
                  <span className="text-[10px] font-bold uppercase tracking-[0.15em] px-1.5 py-0.5 bg-[#0055FF]/10 text-[#0055FF]">
                    System of Record
                  </span>
                </div>
                {info?.sql_server?.connected ? (
                  <span className="flex items-center gap-1 text-xs text-[#16A34A]">
                    <CheckCircle size={14} weight="fill" /> Connected
                  </span>
                ) : (
                  <span className="flex items-center gap-1 text-xs text-[#DC2626]">
                    <XCircle size={14} weight="fill" /> Disconnected
                  </span>
                )}
              </div>
              {/* 2026-08 incident hardening: login + connection-string rows
                  removed — the API no longer returns them (credentials and
                  DSN structure are not for the SPA, even masked). */}
              <Row k="Host" v={info?.sql_server?.host} />
              <Row k="Port" v={info?.sql_server?.port} />
              <Row k="Server name" v={info?.sql_server?.server_name} />
              <Row k="Database" v={info?.sql_server?.database} />
              <Row k="Driver" v={info?.sql_server?.driver} />
              <Row k="Version" v={info?.sql_server?.version} />
              {info?.sql_server?.error && (
                <div className="mt-2 p-2 bg-[#FEF2F2] border border-[#DC2626] text-xs text-[#991B1B] font-mono">
                  {info.sql_server.error}
                </div>
              )}
            </div>

            {/* MongoDB */}
            <div className={card} data-testid="mongo-card">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Database size={18} className="text-[#16A34A]" weight="duotone" />
                  <h2 className="text-base font-bold text-[#0A0A0A]">MongoDB</h2>
                  <span className="text-[10px] font-bold uppercase tracking-[0.15em] px-1.5 py-0.5 bg-[#F5F5F5] text-[#525252]">
                    Cache / Misc
                  </span>
                </div>
                {info?.mongo?.connected ? (
                  <span className="flex items-center gap-1 text-xs text-[#16A34A]">
                    <CheckCircle size={14} weight="fill" /> Connected
                  </span>
                ) : (
                  <span className="flex items-center gap-1 text-xs text-[#DC2626]">
                    <XCircle size={14} weight="fill" /> Disconnected
                  </span>
                )}
              </div>
              <Row k="Host" v={info?.mongo?.host} />
              <Row k="Port" v={info?.mongo?.port} />
              <Row k="Database" v={info?.mongo?.database} />
              <Row k="Version" v={info?.mongo?.version} />
              {info?.mongo?.error && (
                <div className="mt-2 p-2 bg-[#FEF2F2] border border-[#DC2626] text-xs text-[#991B1B] font-mono">
                  {info.mongo.error}
                </div>
              )}
            </div>

            <div className="text-[11px] text-[#A3A3A3] pt-2">
              Want to know exactly what data lives where? See the{" "}
              <a className="text-[#0055FF] underline" href="/docs/data-storage-map">
                Data Storage Map
              </a>.
            </div>
          </>
        )}
      </div>
    </div>
  );
}
